import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { ProductImage } from "@/components/store/ProductCard";

export const QtyControl = ({ value, onChange, testid }) => (
  <div className="flex items-center rounded-full border border-[#E5E7EB] h-10">
    <button data-testid={`${testid}-dec`} onClick={() => onChange(value - 1)} className="w-10 h-10 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
    <span data-testid={`${testid}-value`} className="w-6 text-center text-sm font-semibold">{value}</span>
    <button data-testid={`${testid}-inc`} onClick={() => onChange(value + 1)} className="w-10 h-10 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
  </div>
);

export default function CartDrawer() {
  const { t, pick, money } = useI18n();
  const { items, open, setOpen, setQty, remove, subtotal, key } = useCart();
  const nav = useNavigate();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#0B0D0F]/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside data-testid="cart-drawer" className="absolute top-0 bottom-0 end-0 w-full sm:w-[420px] bg-white flex flex-col shadow-2xl rise">
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#E5E7EB]">
          <h2 className="font-semibold text-lg">{t("cart.title")}</h2>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
        </div>
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag className="w-12 h-12 text-[#D1D5DB]" />
            <p className="muted">{t("cart.empty")}</p>
            <Link to="/products" onClick={() => setOpen(false)} className="btn-dark">{t("cart.browse")}</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto divide-y divide-[#F3F4F6] px-6">
              {items.map((i) => (
                <div key={key(i)} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 py-5">
                  <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0"><ProductImage path={i.image} alt="" /></div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="text-sm font-medium line-clamp-2">{pick(i.name)}</p>
                    {i.variant_name && <p className="text-xs muted">{pick(i.variant_name)}</p>}
                    <div className="flex items-center justify-between">
                      <QtyControl value={i.qty} onChange={(q) => setQty(key(i), q)} testid={`cart-qty-${i.product_id}`} />
                      <span className="font-semibold text-sm">{money(i.price * i.qty)}</span>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(key(i))} className="self-start p-1 text-[#9CA3AF] hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-[#E5E7EB] space-y-4">
              <div className="flex justify-between text-sm"><span className="muted">{t("cart.subtotal")}</span><span data-testid="cart-subtotal" className="font-bold">{money(subtotal)}</span></div>
              <p className="text-xs muted">{t("cart.calc_note")}</p>
              <button data-testid="cart-checkout-btn" onClick={() => { setOpen(false); nav("/checkout"); }} className="btn-primary w-full">{t("cart.checkout")}</button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
